import React from "react";
import { ArrowRight, File, LayoutGrid, Loader2 } from "lucide-react";
import { formatRelativeTime } from "@/utils/formatTime";
import OverviewCard from "./OverviewCard";
import EmptyState from "./EmptyState";
import FileRow from "./FileRow";
import type { Deliverable, DeliverableGroup } from "./utils/deliverables";

interface DeliverablesCardProps {
  deliverables: Deliverable[];
  isLoading: boolean;
  /** Activates a tab, for click-through. */
  onOpenTab: (tabValue: string) => void;
}

const GROUP_HEADINGS: Record<DeliverableGroup, string> = {
  inTab: "In a tab",
  downloadOnly: "Download only",
  notGenerated: "Not generated yet",
};

const GROUP_ORDER: DeliverableGroup[] = [
  "inTab",
  "downloadOnly",
  "notGenerated",
];

/**
 * Every artifact TACO has written for this project, grouped by how the user can
 * reach it, each row clicking through to its tab.
 *
 * The "Not generated yet" group is deliberate: naming an artifact that doesn't
 * exist tells the user what TACO could still produce, which is more useful than
 * silently omitting it.
 */
const DeliverablesCard: React.FC<DeliverablesCardProps> = ({
  deliverables,
  isLoading,
  onOpenTab,
}) => {
  const generatedCount = deliverables.filter(
    (d) => d.group !== "notGenerated",
  ).length;

  return (
    <OverviewCard
      icon={<LayoutGrid className="h-3.5 w-3.5" />}
      title="Deliverables"
      count={isLoading ? undefined : generatedCount}
    >
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-8">
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          <span className="text-xs text-muted-foreground">
            Loading deliverables...
          </span>
        </div>
      ) : deliverables.length === 0 ? (
        <EmptyState>
          No deliverables yet. They&apos;ll appear here as TACO writes them,
          each with its own tab.
        </EmptyState>
      ) : (
        GROUP_ORDER.map((group) => {
          const rows = deliverables.filter((d) => d.group === group);
          if (rows.length === 0) return null;

          return (
            <div key={group}>
              <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {GROUP_HEADINGS[group]}
              </div>
              {rows.map((row) => (
                <FileRow
                  key={row.path ?? row.label}
                  // The tab's own glyph, so a row reads as the same thing as the
                  // tab it opens. Additional assets have no dedicated glyph, so
                  // they get the generic file icon their tabs use.
                  icon={row.icon ?? <File className="h-4 w-4" />}
                  label={row.label}
                  badge={
                    row.extension ? (
                      <span className="flex-none text-[9.5px] font-bold uppercase tracking-wide border border-border rounded px-1.5 text-muted-foreground">
                        {row.extension}
                      </span>
                    ) : undefined
                  }
                  meta={
                    row.group === "notGenerated"
                      ? "—"
                      : formatRelativeTime(row.lastModified)
                  }
                  // Only in-tab rows get an affordance, because only they do
                  // something when clicked. A download glyph here would be a
                  // promise the row cannot keep: downloading is not built yet,
                  // and a non-interactive row shows `trailing` permanently
                  // rather than on hover, so it looked especially clickable. The
                  // "Download only" group heading already conveys the same fact.
                  trailing={
                    row.group === "inTab" ? (
                      <ArrowRight className="h-3.5 w-3.5" />
                    ) : undefined
                  }
                  muted={row.group === "notGenerated"}
                  onClick={
                    row.tabValue && row.group === "inTab"
                      ? () => onOpenTab(row.tabValue as string)
                      : undefined
                  }
                />
              ))}
            </div>
          );
        })
      )}
    </OverviewCard>
  );
};

export default DeliverablesCard;
